import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Heading,
  ResponsiveRender,
  Stack,
  Text,
  TextLink,
  useResponsiveValue
} from '@machobear/design-system'

import emailIcon from '@/features/Company/icons/email-icon.png'
import globeIcon from '@/features/Company/icons/globe-icon.png'
import markerIcon from '@/features/Company/icons/marker-icon.png'
import Head from '@/components/head'

import heroBanner from './hero.png'
import titleLogo from './logo.png'
import qrCode from './qr-code.png'

const MetcapLivingProfile = ({
  metadata,
  footerOnScreen,
  listingsOnScreen
}) => {
  const { t } = useTranslation('metcapliving')
  const profile = {
    banner: heroBanner.src,
    title: t('metcapliving:MetCap Living'),
    url: '/company/metcapliving'
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const isDesktop = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true
  })

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        description={profile.pageDescription}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <Box
        style={{ height: isMobile ? '210px' : isDesktop ? '575px' : '350px' }}
        marginBottom="gutter"
        overflow="hidden"
        position="relative"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="full"
          width="full"
          overflow="hidden"
        >
          <Image
            fill
            priority
            src={heroBanner.src}
            style={{
              objectFit: 'cover',
              objectPosition: 'left'
            }}
            sizes="(max-width: 768px) 768px, (max-width: 1200px) 1200px"
            alt=""
          />
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        width="full"
        marginBottom="large"
      >
        <Stack space="medium">
          <Box display="flex" alignItems="center" marginRight="large">
            <Box
              marginRight="xsmall"
              marginLeft="medium"
              style={{ flexShrink: 0 }}
            >
              <Image
                src={titleLogo.src}
                width={60}
                height={39}
                alt="Metcap Living"
              />
            </Box>
            <Heading level="3" weight="weak">
              {t('metcapliving:MetCap Living')}
            </Heading>
          </Box>
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            gap={{ mobile: 'gutter', tablet: 'none' }}
            marginX="medium"
            marginBottom="xlarge"
            marginTop="medium"
            style={{
              maxWidth: '1096px'
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              marginRight="xxlarge"
              style={{
                width: isMobile ? '100%' : 'fit-content'
              }}
            >
              <Stack space="medium">
                <Stack space="gutter">
                  <Text size="standard" weight="strong">
                    {t('metcapliving:subtitle')}
                  </Text>
                  <Text size="standard">{t('metcapliving:description')}</Text>
                </Stack>
              </Stack>
            </Box>
            <Box
              background="neutralSoft"
              borderRadius="small"
              paddingY="small"
              paddingX="gutter"
              marginTop={{ mobile: 'gutter', tablet: 'none' }}
              style={{
                height: 'min-content',
                width: isMobile ? '100%' : 'fit-content',
                minWidth: '300px'
              }}
            >
              <Stack space="medium">
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={globeIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href="https://www.metcap.com/"
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    www.metcap.ca
                  </TextLink>
                </Box>
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={emailIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href={`mailto:leasing@metcap.com`}
                    rel="noreferrer noopener"
                  >
                    leasing@metcap.com
                  </TextLink>
                </Box>
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={markerIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    target="_blank"
                    href="https://maps.app.goo.gl/ckrQVQXE7wNn99Ds7"
                    rel="noreferrer noopener"
                  >
                    260 Richmond Street East, Suite 300 Toronto, Ontario M5A 1P4
                    (Headquarters)
                  </TextLink>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>
      <Box textAlign="center" paddingBottom="gutter">
        <Heading level="6">{t('metcapliving:Our Portfolio')}</Heading>
      </Box>
      <ResponsiveRender above="tablet">
        <Box
          position={footerOnScreen && listingsOnScreen ? 'absolute' : 'fixed'}
          zIndex="dropdown"
          border="borderNeutralLight"
          background="bodyWhite"
          display="flex"
          style={{
            bottom: '20px',
            right: '10px'
          }}
          paddingBottom="xsmall"
        >
          <Box
            display="flex"
            flexDirection="column"
            position="relative"
            justifyContent="center"
            style={{ width: '158px', height: '175px' }}
          >
            <Image
              src={qrCode}
              alt="MetCap Living Qr"
              width={160}
              height={160}
              placeholder="blur"
            />
            <Text align="center" size="xsmall" weight="medium">
              {t('vancouverpropertymanagementmaster:QR description')}
            </Text>
          </Box>
        </Box>
      </ResponsiveRender>
    </>
  )
}

export default MetcapLivingProfile
